/**
 * Tool approval query operations - Get
 */

import type { UserId } from '../../types.js';
import { getSupabaseClient } from '../../supabase-client.js';
import type { StoredToolApproval, QueryOptions, QueryResult } from '../types.js';
import { logger } from '../../../logger.js';

/**
 * Get a single approval by ID
 */
export async function getApprovalById(approvalId: string): Promise<StoredToolApproval | null> {
  try {
    const client = getSupabaseClient();
    const { data, error } = await client
      .from('meridian_tool_approvals')
      .select('*')
      .eq('id', approvalId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null;
      }
      throw error;
    }

    return data as StoredToolApproval;
  } catch (error) {
    logger.error('error getting approval by id', error, { op: 'getApprovalById' });
    throw error;
  }
}

/**
 * Get all approvals for a user
 */
export async function getApprovalsByUser(
  userId: UserId,
  status?: 'pending' | 'approved' | 'denied' | 'expired',
  options?: QueryOptions
): Promise<QueryResult<StoredToolApproval>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    let query = client
      .from('meridian_tool_approvals')
      .select('*', { count: 'exact' })
      .eq('user_id', userId);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error, count } = await query
      .order('requested_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredToolApproval[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting approvals by user', error, { op: 'getApprovalsByUser' });
    throw error;
  }
}

/**
 * Get all approvals for a specific tool
 */
export async function getApprovalsByTool(
  toolName: string,
  options?: QueryOptions
): Promise<QueryResult<StoredToolApproval>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    const { data, error, count } = await client
      .from('meridian_tool_approvals')
      .select('*', { count: 'exact' })
      .eq('tool_name', toolName)
      .order('requested_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredToolApproval[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting approvals by tool', error, { op: 'getApprovalsByTool' });
    throw error;
  }
}

/**
 * Get all approvals expiring before a specific time
 */
export async function getApprovalsByExpiration(
  expiresBeforeMs: number,
  options?: QueryOptions
): Promise<QueryResult<StoredToolApproval>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    const { data, error, count } = await client
      .from('meridian_tool_approvals')
      .select('*', { count: 'exact' })
      .lte('expires_at', expiresBeforeMs)
      .eq('status', 'pending')
      .order('expires_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredToolApproval[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting approvals by expiration', error, { op: 'getApprovalsByExpiration' });
    throw error;
  }
}
